'use client';

import Grid2 from '@mui/material/Unstable_Grid2';

import { nanoid } from 'nanoid';

import ChipTicketStatus from '@/components/chip/ChipTicketStatus';
import EntityDetailContainer from '@/components/entity-detail/components/Container/EntityDetailContainer';
import TextAction from '@/components/text-action/TextAction';
import { DisplayField } from '@/components/typography/DisplayField';
import type { TicketResponse } from '@/types/apps/ticket.type';
import { CompletionStatusOptions } from '@/types/enums/completion-status.enum';
import { StatusTicketEnum } from '@/types/enums/status-ticket.enum';
import { dateUtils } from '@/utils/date';
import { t } from '@/utils/lang';
import { routeManager } from '@/utils/route';

interface TicketInfoProps {
    data: TicketResponse | null;
}

const entityDetailIdentifier = `${routeManager.TICKET_DETAIL}/${nanoid()}`;

const TicketInfo: React.FC<TicketInfoProps> = ({ data }) => {
    const statusLabel =
        CompletionStatusOptions.find(
            opt => opt.value === data?.completionStatus
        )?.label || '-:-';

    return (
        <EntityDetailContainer identifier={entityDetailIdentifier}>
            <Grid2 container spacing={6}>
                <DisplayField
                    xs={4}
                    label={
                        <TextAction value={data?.code} copy>
                            {t('Mã Ticket')}
                        </TextAction>
                    }
                    value={data?.code}
                />
                <DisplayField
                    xs={4}
                    label={
                        <TextAction value={data?.ticketType?.name} copy>
                            {t('Loại Ticket')}
                        </TextAction>
                    }
                    value={data?.ticketType?.name}
                />
                <DisplayField
                    xs={4}
                    label={
                        <TextAction value={data?.name} copy>
                            {t('Tên Ticket')}
                        </TextAction>
                    }
                    value={data?.name}
                />
                <DisplayField
                    xs={4}
                    label={
                        <TextAction value={data?.ticketCreator} copy>
                            {t('Người tạo', 'TicketDetail')}
                        </TextAction>
                    }
                    value={data?.ticketCreator}
                />
                <DisplayField
                    xs={4}
                    label={
                        <TextAction value={data?.assigneeName} copy>
                            {t('Người phụ trách Ticket')}
                        </TextAction>
                    }
                    value={data?.assigneeName}
                />
                <DisplayField
                    xs={4}
                    label={
                        <TextAction value={data?.customerContact?.name} copy>
                            {t('Tên người liên hệ')}
                        </TextAction>
                    }
                    value={data?.customerContact?.name}
                />

                <DisplayField
                    xs={4}
                    label={
                        <TextAction
                            value={dateUtils.getDate(data?.createdDate || '')}
                            copy
                        >
                            {t('Ngày tạo')}
                        </TextAction>
                    }
                    value={dateUtils.getDate(data?.createdDate || '')}
                />
                <DisplayField
                    xs={4}
                    label={
                        <TextAction
                            value={dateUtils.getDate(data?.scheduledTime || '')}
                            copy
                        >
                            {t('Ngày bắt đầu Ticket')}
                        </TextAction>
                    }
                    value={dateUtils.getDate(data?.scheduledTime || '')}
                />
                <DisplayField
                    xs={4}
                    label={
                        <TextAction
                            value={data?.customerContact?.phoneNumber}
                            copy
                        >
                            {t('SDT người liên hệ')}
                        </TextAction>
                    }
                    value={data?.customerContact?.phoneNumber}
                />
                <DisplayField
                    xs={4}
                    label={t('Mức độ ưu tiên')}
                    value={data?.priority?.name}
                />
                <DisplayField
                    xs={4}
                    label={
                        <TextAction value={data?.customer?.name} copy>
                            {t('Tên khách hàng')}
                        </TextAction>
                    }
                    // Don't use EntityButton because if any tab can contain task tab => loop (ex: Trip Form (TASK) -> TICKET -> CUSTOMER -> DEVICE (TASK))
                    value={data?.customer?.name || '-:-'}
                />
                <DisplayField
                    xs={4}
                    label={
                        <TextAction
                            value={data?.address || data?.customer?.address}
                            copy
                            address
                        >
                            {t('Địa chỉ')}
                        </TextAction>
                    }
                    value={data?.address || data?.customer?.address}
                />
                <DisplayField
                    xs={4}
                    label={t('Trạng thái', 'TicketDetail')}
                    value={
                        <ChipTicketStatus
                            size='small'
                            status={
                                (data?.ticketStatus as StatusTicketEnum) ||
                                '-:-'
                            }
                        />
                    }
                />
                <DisplayField
                    xs={
                        data?.completionStatus &&
                        data?.ticketStatus === StatusTicketEnum.CANCEL
                            ? 8
                            : 4
                    }
                    label={
                        <TextAction value={data?.note} copy address>
                            {t('Ghi chú')}
                        </TextAction>
                    }
                    value={data?.note || '-:-'}
                />
                {data?.completionStatus &&
                    data?.ticketStatus === StatusTicketEnum.CLOSED && (
                        <>
                            <DisplayField
                                xs={4}
                                label={t('Trạng thái hoàn thành')}
                                value={statusLabel}
                            />
                            <DisplayField
                                xs={4}
                                label={t('Mô tả kết quả')}
                                value={data?.descriptionResult}
                            />
                        </>
                    )}
                {data?.reasonCancel &&
                    data?.ticketStatus === StatusTicketEnum.CANCEL && (
                        <DisplayField
                            xs={4}
                            label={t('Lý do hủy')}
                            value={data.reasonCancel}
                        />
                    )}
            </Grid2>
        </EntityDetailContainer>
    );
};

export default TicketInfo;
